import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const emptyForm = {
    code: "", discount_pct: 10, scope_type: "all",
    product_ids: [], bundle_ids: [],
    applies_to_products: true, applies_to_bundles: true,
    expires_at: "", max_uses: "", status: "active", notes: "",
};

const codeUpper = (s) => s.toUpperCase().replace(/[^A-Z0-9_-]+/g, "");

export default function AdminCoupons() {
    const [coupons, setCoupons] = useState([]);
    const [products, setProducts] = useState([]);
    const [bundles, setBundles] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [statsOpen, setStatsOpen] = useState(false);
    const [statsData, setStatsData] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    const load = async () => {
        try {
            const [c, p, b] = await Promise.all([
                api.get("/admin/coupons"),
                api.get("/admin/products"),
                api.get("/admin/bundles"),
            ]);
            setCoupons(c.data || []);
            setProducts(p.data || []);
            setBundles(b.data || []);
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
    const openEdit = (c) => {
        setEditing(c);
        setForm({
            code: c.code, discount_pct: c.discount_pct, scope_type: c.scope_type || "all",
            product_ids: c.product_ids || [], bundle_ids: c.bundle_ids || [],
            applies_to_products: c.applies_to_products !== false,
            applies_to_bundles: c.applies_to_bundles !== false,
            expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
            max_uses: c.max_uses ?? "", status: c.status || "active", notes: c.notes || "",
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                discount_pct: Number(form.discount_pct),
                max_uses: form.max_uses === "" ? null : Number(form.max_uses),
                expires_at: form.expires_at
                    ? new Date(form.expires_at + "T23:59:59Z").toISOString()
                    : null,
            };
            if (editing) {
                await api.patch(`/admin/coupons/${editing.id}`, payload);
                toast.success("Coupon updated");
            } else {
                await api.post("/admin/coupons", payload);
                toast.success("Coupon created");
            }
            setOpen(false); setEditing(null); setForm(emptyForm);
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete coupon "${c.code}"? (previous redemptions are kept in the history)`)) return;
        try { await api.delete(`/admin/coupons/${c.id}`); toast.success("Coupon deleted"); await load(); }
        catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const openStats = async (c) => {
        setStatsOpen(true); setStatsLoading(true); setStatsData(null);
        try {
            const { data } = await api.get(`/admin/coupons/${c.id}/stats`);
            setStatsData(data);
        } catch (err) {
            toast.error(apiErrorMessage(err));
            setStatsOpen(false);
        } finally { setStatsLoading(false); }
    };

    const toggleId = (list, id) => list.includes(id) ? list.filter(x => x !== id) : [...list, id];

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-coupons-page">
                <div>
                    <div className="label-mono mb-3">/ Coupons</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Discount coupons</h1>
                    <p className="text-avx-muted mt-2 max-w-2xl">
                        Promo codes to use at checkout. 1 use per user. They apply to Flow and PayPal.
                    </p>
                </div>
                <button onClick={openCreate} className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="create-coupon-btn">
                    + New coupon
                </button>
            </div>

            {coupons.length === 0 ? (
                <div className="panel p-10 text-center text-avx-muted">No coupons yet.</div>
            ) : (
                <div className="panel overflow-hidden">
                    <table className="w-full text-sm" data-testid="coupons-table">
                        <thead className="bg-avx-bg/60 border-b border-avx-line">
                            <tr className="text-left label-mono">
                                <th className="px-4 py-3">Code</th>
                                <th className="px-4 py-3">% Off</th>
                                <th className="px-4 py-3">Applies to</th>
                                <th className="px-4 py-3">Uses</th>
                                <th className="px-4 py-3">Expires</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {coupons.map((c) => (
                                <tr key={c.id} className="border-b border-avx-line/60 hover:bg-avx-bg/30" data-testid={`coupon-row-${c.id}`}>
                                    <td className="px-4 py-3 font-mono font-bold text-avx-blue">{c.code}</td>
                                    <td className="px-4 py-3 font-mono text-avx-success">{c.discount_pct}%</td>
                                    <td className="px-4 py-3 text-xs">
                                        {c.scope_type === "all"
                                            ? <span className="text-avx-muted">All</span>
                                            : <span>{(c.product_ids?.length || 0) + (c.bundle_ids?.length || 0)} items</span>}
                                        <span className="text-avx-muted ml-2">
                                            {c.applies_to_products && "P "}{c.applies_to_bundles && "B"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs">{c.uses_count || 0}{c.max_uses ? `/${c.max_uses}` : ""}</td>
                                    <td className="px-4 py-3 font-mono text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[9px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm ${c.status === "active" ? "bg-avx-success/15 text-avx-success" : "bg-avx-line text-avx-muted"}`}>
                                            {c.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => openStats(c)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-success hover:underline mr-3" data-testid={`stats-coupon-${c.id}`}>Stats</button>
                                        <button onClick={() => openEdit(c)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-blue hover:underline mr-3" data-testid={`edit-coupon-${c.id}`}>Edit</button>
                                        <button onClick={() => remove(c)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-danger hover:underline" data-testid={`delete-coupon-${c.id}`}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-avx-surface border-avx-line max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{editing ? "Edit coupon" : "New coupon"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4" data-testid="coupon-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Code</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: codeUpper(e.target.value) })} placeholder="BLACKFRIDAY" required data-testid="coupon-code" />
                            </div>
                            <div>
                                <Label className="label-mono">Discount %</Label>
                                <Input type="number" min="1" max="100" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} required data-testid="coupon-pct" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Scope</Label>
                                <Select value={form.scope_type} onValueChange={(v) => setForm({ ...form, scope_type: v })}>
                                    <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11 font-mono"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All items</SelectItem>
                                        <SelectItem value="specific">Selected only</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-mono">Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11 font-mono"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">Activo</SelectItem>
                                        <SelectItem value="disabled">Deshabilitado</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="flex gap-6">
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.applies_to_products} onChange={(e) => setForm({ ...form, applies_to_products: e.target.checked })} className="accent-avx-blue" />
                                Applies to plugins
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.applies_to_bundles} onChange={(e) => setForm({ ...form, applies_to_bundles: e.target.checked })} className="accent-avx-blue" />
                                Applies to bundles
                            </label>
                        </div>

                        {form.scope_type === "specific" && (
                            <>
                                {form.applies_to_products && (
                                    <div>
                                        <Label className="label-mono">Selected plugins ({form.product_ids.length})</Label>
                                        <div className="mt-2 panel p-3 max-h-40 overflow-y-auto space-y-1">
                                            {products.map((p) => (
                                                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-avx-bg/40 rounded-sm">
                                                    <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => setForm({ ...form, product_ids: toggleId(form.product_ids, p.id) })} className="accent-avx-blue" />
                                                    <span className="font-mono text-xs">{p.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {form.applies_to_bundles && (
                                    <div>
                                        <Label className="label-mono">Selected bundles ({form.bundle_ids.length})</Label>
                                        <div className="mt-2 panel p-3 max-h-40 overflow-y-auto space-y-1">
                                            {bundles.map((b) => (
                                                <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-avx-bg/40 rounded-sm">
                                                    <input type="checkbox" checked={form.bundle_ids.includes(b.id)} onChange={() => setForm({ ...form, bundle_ids: toggleId(form.bundle_ids, b.id) })} className="accent-avx-blue" />
                                                    <span className="font-mono text-xs">{b.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Expires (optional)</Label>
                                <Input type="date" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="coupon-expires" />
                            </div>
                            <div>
                                <Label className="label-mono">Max total uses (optional)</Label>
                                <Input type="number" min="1" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Unlimited" data-testid="coupon-max-uses" />
                            </div>
                        </div>

                        <div>
                            <Label className="label-mono">Internal notes</Label>
                            <Textarea rows={2} className="mt-2 bg-avx-bg border-avx-line" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="BlackFriday 2026 campaign · influencers only" />
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-avx-line">
                            <button type="button" onClick={() => setOpen(false)} className="btn-secondary px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">Cancel</button>
                            <button type="submit" className="btn-primary px-6 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="coupon-submit">
                                {editing ? "Save changes" : "Create coupon"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
                <DialogContent className="bg-avx-surface border-avx-line max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            Stats · <span className="font-mono text-avx-blue">{statsData?.coupon?.code || "…"}</span>
                        </DialogTitle>
                    </DialogHeader>
                    {statsLoading || !statsData ? (
                        <div className="py-12 text-center text-avx-muted font-mono text-sm">Loading…</div>
                    ) : (
                        <div className="space-y-5" data-testid="coupon-stats">
                            <div className="grid grid-cols-3 gap-3">
                                <StatCard label="Uses" value={statsData.totals.uses_count} extra={statsData.totals.max_uses ? `/ ${statsData.totals.max_uses}` : "/ unlimited"} testId="stat-uses" />
                                <StatCard label="Discount" value={`${statsData.coupon.discount_pct}%`} extra="per use" testId="stat-pct" />
                                <StatCard label="Status" value={statsData.coupon.status} testId="stat-status" />
                            </div>

                            {Object.keys(statsData.totals.by_currency).length > 0 && (
                                <div>
                                    <div className="label-mono mb-2">/ Total discount given</div>
                                    <div className="grid grid-cols-2 gap-3">
                                        {Object.entries(statsData.totals.by_currency).map(([cur, info]) => (
                                            <div key={cur} className="panel p-4">
                                                <div className="label-mono text-[10px] mb-1">{cur}</div>
                                                <div className="font-heading font-bold text-2xl text-avx-success">
                                                    {cur === "CLP" ? `$${info.discount_total.toLocaleString("es-CL", { maximumFractionDigits: 0 })}` : `$${info.discount_total.toFixed(2)}`}
                                                </div>
                                                <div className="text-[10px] font-mono text-avx-muted mt-1">{info.uses} purchase{info.uses === 1 ? "" : "s"}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="label-mono mb-2">/ Latest redemptions</div>
                                {statsData.recent.length === 0 ? (
                                    <div className="panel p-6 text-center text-avx-muted text-sm">This coupon has not been redeemed yet.</div>
                                ) : (
                                    <div className="panel overflow-hidden">
                                        <table className="w-full text-xs">
                                            <thead className="bg-avx-bg/60 border-b border-avx-line label-mono">
                                                <tr className="text-left">
                                                    <th className="px-3 py-2">When</th>
                                                    <th className="px-3 py-2">User</th>
                                                    <th className="px-3 py-2">Purchase</th>
                                                    <th className="px-3 py-2 text-right">Discount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statsData.recent.map((r) => (
                                                    <tr key={r.id} className="border-b border-avx-line/60" data-testid={`redemption-${r.id}`}>
                                                        <td className="px-3 py-2 font-mono">{new Date(r.applied_at).toLocaleString("en-US")}</td>
                                                        <td className="px-3 py-2">
                                                            {r.user ? <><div>{r.user.name}</div><div className="text-avx-muted">{r.user.email}</div></> : <span className="text-avx-muted">—</span>}
                                                        </td>
                                                        <td className="px-3 py-2 font-mono">
                                                            {r.purchase ? <>{r.purchase.status} · {r.purchase.currency}</> : <span className="text-avx-muted">—</span>}
                                                        </td>
                                                        <td className="px-3 py-2 font-mono text-right text-avx-success">
                                                            {r.currency === "CLP"
                                                                ? `$${Number(r.discount_amount).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`
                                                                : `$${Number(r.discount_amount).toFixed(2)}`}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}

function StatCard({ label, value, extra, testId }) {
    return (
        <div className="panel p-4" data-testid={testId}>
            <div className="label-mono text-[10px] mb-1">{label}</div>
            <div className="font-heading font-bold text-2xl">{value}</div>
            {extra && <div className="text-[10px] font-mono text-avx-muted mt-1">{extra}</div>}
        </div>
    );
}
